import { faArrowsRotate } from "@fortawesome/free-solid-svg-icons/faArrowsRotate";
import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons/faTriangleExclamation";
import dayjs from "dayjs";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Info } from "../../../../components/Info";
import { Integration } from "../../../../model/integration/Integration";
import { IntegrationConfigWhatsappMeta } from "../../../../model/integration/types/config/IntegrationConfigWhatsappMeta";
import { integrationSliceSelectors } from "../../../../redux/integration/integration.selector";
import { integrationActions } from "../../../../redux/integration/integration.slice";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";

/** Meta only sends the history within a day of onboarding */
const SYNC_WINDOW_HOURS = 24;
const DONE_SHOWN_FOR_DAYS = 3;
const POLL_MS = 30_000;

type SyncState =
  | { kind: "failed" }
  | { kind: "declined" }
  | { kind: "done" }
  | { kind: "syncing"; progress: number }
  | { kind: "stopped"; progress: number };

const syncStateOf = (
  coexistence: IntegrationConfigWhatsappMeta["coexistence"],
): SyncState | null => {
  if (!coexistence) {
    return null;
  }

  // Only the history request failing loses the chats; contacts alone failing
  // still leaves them coming in
  if (coexistence.syncError && !coexistence.historySyncRequestId) {
    return { kind: "failed" };
  }

  const { history } = coexistence;

  if (history?.declined) {
    return { kind: "declined" };
  }

  if ((history?.progress ?? 0) >= 100) {
    return dayjs().diff(history?.updatedAt, "day") < DONE_SHOWN_FOR_DAYS
      ? { kind: "done" }
      : null;
  }

  if (dayjs().diff(coexistence.onboardedAt, "hour") < SYNC_WINDOW_HOURS) {
    return { kind: "syncing", progress: Math.round(history?.progress ?? 0) };
  }

  // No history at all after the window is a business with nothing to send
  return history?.progress !== undefined
    ? { kind: "stopped", progress: Math.round(history.progress) }
    : null;
};

export const WACoexistenceSyncStatus: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId);
  const integration = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaIntegration,
  ) as Integration<IntegrationConfigWhatsappMeta> | null;

  const state = syncStateOf(integration?.config?.coexistence);
  const syncing = state?.kind === "syncing";

  // The progress only moves on Meta's webhooks, which reach nobody's screen
  useEffect(() => {
    if (!syncing || !userId) {
      return;
    }

    const timer = setInterval(() => {
      dispatch(integrationActions.getUserIntegrations({ userId }));
    }, POLL_MS);

    return () => clearInterval(timer);
  }, [dispatch, syncing, userId]);

  if (!state) {
    return null;
  }

  switch (state.kind) {
    case "failed":
      return (
        <Info
          color="danger"
          icon={faTriangleExclamation}
          width="100%"
          info={t("integration.whatsapp.meta.coexistence.failed")}
        />
      );

    case "declined":
      return (
        <Info
          color="warning"
          width="100%"
          info={t("integration.whatsapp.meta.coexistence.declined")}
        />
      );

    case "done":
      return (
        <Info
          color="success"
          icon={faCheck}
          width="100%"
          info={t("integration.whatsapp.meta.coexistence.done")}
        />
      );

    case "syncing":
      return (
        <Info
          icon={faArrowsRotate}
          width="100%"
          info={t("integration.whatsapp.meta.coexistence.syncing", {
            progress: state.progress,
          })}
        />
      );

    case "stopped":
      return (
        <Info
          color="warning"
          icon={faTriangleExclamation}
          width="100%"
          info={t("integration.whatsapp.meta.coexistence.stopped", {
            progress: state.progress,
          })}
        />
      );
  }
};
